import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/firestore';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, from } from 'rxjs';
import { map, tap } from 'rxjs/operators';

// Serviço para armazenar todas as funções genéricas para operações do FireStore.
@Injectable({
  providedIn: 'root',
})
export class GenericFirebaseRequestsService {
  constructor(
    private firestore: AngularFirestore,
    private spinner: NgxSpinnerService
  ) {}

  /**
   * Função para consultar uma coleção sem dados extras.
   * @param collectionName Nome da coleção que será consultada.
   * @param getDataOnce Booleano para definir que tipo de retorno o request terá. Caso verdadeiro será retornado todos os dados da coleção de uma vez e o observable não será disparado quando houver mudanças nos dados da coleção (Use quando quiser pegar um dado apenas uma vez e não quiser atualização em tempo real dos dados). Caso falso será retornado todos os dados da coleção aos poucos conforme a funcionalidade padrão do valueChanges() e o observable será disparado quando houver mudanças nos dados da aplicação (Use quando quiser pegar um dado mais de uma vez e queira atualização em tempo real dos dados da coleção).
   * @param queryFn Objecto queryFn para adcionar filtros de where, orderBy, etc (Esse parâmetro é opcional).
   * @returns Um Observable do tipo passado.
   */
  getCollection<T>(
    collectionName: string,
    getDataOnce: boolean = false,
    queryFn?: QueryFn,
    loading: boolean = true
  ): Observable<T[]> {
    if (loading) this.spinner.show();
    const collectionRef = queryFn
      ? this.firestore.collection<T>(collectionName, queryFn)
      : this.firestore.collection<T>(collectionName);

    const observable = getDataOnce
      ? collectionRef
          .get()
          .pipe(map((snapshot) => snapshot.docs.map((doc) => doc.data() as T)))
      : collectionRef.valueChanges();
    return observable.pipe(
      tap((res) => {
        if (loading) this.spinner.hide();
      })
    );
  }

  /**
   * Função para consultar uma coleção + dados extras da coleção. Use essa função quando quiser ter acesso aos dados de QueryDocumentSnapshot como id de documentos dentro da coleção, etc.
   * @param collectionName Nome da coleção a ser consultada.
   * @param queryFn Objecto queryFn para adcionar filtros de where, orderBy, etc (Esse parâmetro é opcional).
   * @returns Um Observable de QueryDocumentSnapshot do tipo passado.
   */
  getCollectionInfo<T>(
    collectionName: string,
    queryFn?: QueryFn,
    loading: boolean = true
  ) {
    if (loading) this.spinner.show();
    const collectionRef = queryFn
      ? this.firestore.collection<T>(collectionName, queryFn)
      : this.firestore.collection<T>(collectionName);

    return collectionRef.get().pipe(
      tap((res) => {
        this.spinner.hide();
      })
    );
  }

  /**
   * Função para consultar um documento.
   * @param collectionName Nome da coleção em que o documento está.
   * @param documentId Id do documento que será consultado.
   * @param getDataOnce Booleano para definir que tipo de retorno o request terá. Caso verdadeiro será retornado todos os dados do documento de uma vez e o observable não será disparado quando houver mudanças nos dados da documento (Use quando quiser pegar um dado apenas uma vez e não quiser atualização em tempo real dos dados). Caso falso será retornado todos os dados do documento aos poucos conforme a funcionalidade padrão do valueChanges() e o observable será disparado quando houver mudanças nos dados da aplicação (Use quando quiser pegar um dado mais de uma vez e queira atualização em tempo real dos dados da coleção).
   * @returns Um Observable do tipo passado.
   */
  getDocument<T>(
    collectionName: string,
    documentId: string,
    getDataOnce: boolean = false,
    loading: boolean = true
  ): Observable<T> {
    if (loading) this.spinner.show();
    const documentPath = `${collectionName}/${documentId}`;
    const documentRef = this.firestore.doc<T>(documentPath);
    const observable = getDataOnce
      ? documentRef.get().pipe(
          map(
            (
              doc: firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>
            ) => {
              const data = doc.data();
              return data as T;
            }
          )
        )
      : (documentRef.valueChanges() as Observable<T>);

    return observable.pipe(
      tap((res) => {
        if (loading) this.spinner.hide();
      })
    );
  }

  /**
   * Função para consultar um documento + dados extras do documento.
   * @param collectionName Nome da coleção em que o documento está.
   * @param documentId Id do documento que será consultado.
   * @returns Um Observable de DocumentSnapshot do tipo passado.
   */
  getDocumentInfo<T>(
    collectionName: string,
    documentId: string,
    loading: boolean = true
  ) {
    if (loading) this.spinner.show();
    return this.firestore
      .doc<T>(`${collectionName}/${documentId}`)
      .get()
      .pipe(
        tap((res) => {
          if (loading) this.spinner.hide();
        })
      );
  }

  /**
   * Função para setar o valor de um documento.
   * @param collectionName Nome da coleção em que o documento está.
   * @param documentId Id do documento que será setado um novo valor.
   * @param valueToSet Valor a ser setado.
   * @returns Observable de void.
   */
  setDocument<T>(
    collectionName: string,
    documentId: string,
    valueToSet: T,
    loading: boolean = true
  ): Observable<void> {
    if (loading) this.spinner.show();
    const documentPath = `${collectionName}/${documentId}`;
    return from(
      this.firestore.doc<T>(documentPath).set(valueToSet, { merge: true })
    ).pipe(
      tap((res) => {
        if (loading) this.spinner.hide();
      })
    );
  }

  /**
   * Função para atualizar o valor de um documento.
   * @param collectionName Nome da coleção em que o documento está.
   * @param documentId Id do documento que será atualizado.
   * @param valueToUpdate Valor a ser atualizado.
   * @returns Observable de void.
   */
  updateDocument<T>(
    collectionName: string,
    documentId: string,
    valueToUpdate: T,
    loading: boolean = true
  ): Observable<void> {
    if (loading) this.spinner.show();
    const documentPath = `${collectionName}/${documentId}`;
    return from(this.firestore.doc<T>(documentPath).update(valueToUpdate)).pipe(
      tap((res) => {
        if (loading) this.spinner.hide();
      })
    );
  }
}
